import React from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useAuthContext } from '@src/providers/AuthProvider'
import { analyticsService } from '@src/services/analytics'
import { createContextLogger } from '@src/utils'

const logger = createContextLogger('ExchangeButton')

export interface ExchangeButtonProps {
    onExchange: () => void
    isSessionTokenReady?: boolean
    isQuoteFetching?: boolean
    isQuoteReady?: boolean
    requiredChainType?: 'EVM' | 'Solana' | 'Tron'
    isSellValueZero?: boolean
}

const ExchangeButton: React.FC<ExchangeButtonProps> = ({
    onExchange,
    isSessionTokenReady = false,
    isQuoteFetching = false,
    isQuoteReady = false,
    requiredChainType = 'EVM',
    isSellValueZero = false,
}) => {
    const { connectHandler } = useAuthContext()
    const { isSolanaConnected, isTronConnected, isEVMConnected } = useWalletConnections()

    // Check if wallet is connected based on the required chain type
    const isChainConnected = (): boolean => {
        switch (requiredChainType) {
            case 'Solana':
                return isSolanaConnected
            case 'Tron':
                return isTronConnected
            default: // EVM
                return isEVMConnected
        }
    }

    const needsWalletConnection = !isChainConnected()
    const isLoading = isQuoteFetching || (isSessionTokenReady === false && !needsWalletConnection)

    // Button is disabled unless all conditions are met:
    // 1. Session token is ready
    // 2. Not currently fetching a quote
    // 3. Sell value is not zero/empty
    // 4. A valid quote is ready
    // Always enabled for wallet connection
    const buttonDisabled = needsWalletConnection
        ? false
        : !isSessionTokenReady || isQuoteFetching || isSellValueZero || !isQuoteReady

    const buttonClass = cn(
        'w-full h-14 flex select-none items-center justify-center space-x-[10px] rounded-2xl px-[18px] py-[14px] font-medium leading-7 transition-all',
        {
            // For wallet connection, use gray background to match SwapButton
            'bg-mach-component-background': needsWalletConnection || (!needsWalletConnection && buttonDisabled),
            'bg-mach-button-color hover:brightness-110': !needsWalletConnection && !buttonDisabled,
        }
    )

    const handleConnectWallet = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()

        // Track the main wallet connection attempt
        analyticsService.trackEvent('wallet_main_attempt_connect', {
            wallet_type: requiredChainType,
            button_location: 'fiat_exchange', // This is from the fiat exchange screen
            timestamp: new Date().toISOString(),
        })

        // Use the AuthProvider to connect the appropriate wallet
        connectHandler()
        logger.info(`Connecting ${requiredChainType} wallet`)
    }

    // Determine which button handler to use
    const handleClick = needsWalletConnection ? handleConnectWallet : onExchange

    // Determine button text
    const getButtonText = () => {
        if (needsWalletConnection) {
            return `Connect ${requiredChainType} Wallet`
        }

        if (!isSessionTokenReady) {
            return 'Loading Session...'
        }

        if (isQuoteFetching) {
            return 'Getting Quote...'
        }

        if (isSellValueZero) {
            return 'Enter Amount'
        }

        if (!isQuoteReady) {
            return 'Preparing Exchange...'
        }

        return 'Exchange Now'
    }

    return (
        <button className={buttonClass} disabled={buttonDisabled} onClick={handleClick}>
            <div className='flex items-center justify-center gap-2'>
                {isLoading && !needsWalletConnection && <Loader2 className='h-5 w-5 animate-spin' />}
                {needsWalletConnection && (
                    <>
                        {requiredChainType === 'EVM' && (
                            <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                        )}
                        {requiredChainType === 'Solana' && (
                            <img src={'assets/network/solana.png'} alt='solana' className='h-6 w-6' />
                        )}
                        {requiredChainType === 'Tron' && (
                            <img src={'assets/network/tron.png'} alt='tron' className='h-6 w-6' />
                        )}
                    </>
                )}
                {getButtonText()}
            </div>
        </button>
    )
}

export default ExchangeButton
